<template>
  <div class='ilot'>
    <v-breadcrumb>
      <button v-can:edit-exploitation class="primary-action flex-12 btn-icon enregistrer" type="button" @click="save(true)">Enregistrer</button>
    </v-breadcrumb>

    <v-row>
      <v-field-text v-model="nom" label="N° ou nom" placeholder="ex : DP04Z" name="nom" v-validate />
      <v-field-integer v-model="sau" label="SAU (Surface Agricole Utile) en ha" placeholder="ex : 3600" v-validate="'required|min_value:0'" ref="sol_sau" name="sau" data-vv-as="SAU" />
    </v-row>
    <v-row>
      <div>
        <input id='non_irrigue' :checked='non_irrigue' type='checkbox' @click.prevent='clearIrrigationData' :disabled="$can.not('edit-exploitation')">
        <label for ='non_irrigue'>Ilot non irrigué</label>
      </div>
    </v-row>

    <v-module title="Types(s) de sol">
      <v-error-ct :error="errors.first('somme_surface_ilot')">
        <SolIlotTable :sols="sols" @deleteRow="deleteSol" @addRow="addSol" />
        <input type="hidden" :value="somme_surface_sol" name="somme_surface_ilot" v-validate.initial="'confirmed:sol_sau'" />
        <input type="hidden"
               :value="solsString"
               name="sols"
               v-validate />
      </v-error-ct>
    </v-module>

    <v-module title="Matériel" :disable="non_irrigue">
      <v-row  v-if="!non_irrigue">
        <v-field-select v-model="id_ref_type_materiel" label="Type de matériel" :options="typeMateriel" @select="materiel => charge_fixe = materiel.charge_fixe" labelField="nom" valueField="id_ref_type_materiel" name="ref_type_materiel" v-validate/>
        <v-field-integer v-model="nombre_materiel" v-if="isEnrouleur" label="Nombre d'enrouleur(s)" placeholder="ex : 3" name="nb_enrouleur" data-vv-as="nombre d'enrouleur" v-validate.initial="{ min_value : 1 }" />
      </v-row>
      <v-row v-if='!non_irrigue'>
        <v-field-integer v-validate.initial='"required|min_value:0"' v-model='debit_materiel' label='Débit en m³/heure' placeholder='ex : 250' name='debit_materiel' data-vv-as='Débit' />
        <v-field-integer v-validate.initial='"required|between:0,24"' v-model='utilisation_materiel' label='Durée d"utilisation en heures/jour' placeholder='ex : 16' name='utilisation_materiel' data-vv-as='Durée utilisation' />
        <v-field-integer v-model='resultat_utilisation' readonly label="Volume d'eau disponible pour l'irrigation en m³/jour" placeholder='ex : 4000' />
        <v-field-float :step="0.01" v-validate.initial='"required|min_value:0|decimal:2"' v-model='charge_fixe' label="Charges fixes d'irrigation en €/ha" placeholder='ex : 5.20' name='charge_fixe' data-vv-as='charges fixes' :tooltip="true"/>
      </v-row>
    </v-module>

    <v-module title="Prix de l'eau" :disable='non_irrigue'>
      <CalculPrixEau v-if='!non_irrigue' v-model="id_ref_prix_eau" :coutConso="coutConso" />
    </v-module>

    <v-module title="Irrigation" :disable="non_irrigue">
      <v-row  v-if="!non_irrigue">
        <v-field-float v-model="prix_irrigation" :step="0.01" label="Charges opérationnelles d'irrigation (en cts/m³)" placeholder="calculez ci-dessus" name="prix_irrigation" v-validate="{ decimal : 2 }" :tooltip="true"/>
        <v-field-integer v-model="volume_irrigation" label="Volume d'eau disponible en m³" placeholder="ex : 160 000" name="volume_irrigation" v-validate.initial='"required|min_value:0"' data-vv-as="volume d'eau"/>
      </v-row>
      <v-row v-if="!non_irrigue">
        <v-field-date v-model="date_debut_irrigation" label="Date de début d'interdiction d'irrigation" placeholder="ex : ex : 15/01/2018" name="date_debut_irrigation" v-validate />
        <v-field-date v-model="date_fin_irrigation" label="Date de fin d'interdiction d'irrigation" placeholder="ex : 16/03/2018" name="date_fin_irrigation" v-validate />
      </v-row>
    </v-module>
  </div>
</template>

<script>
import SolIlotTable from '@/components/SolIlotTable'
import mixinConfirmation from '@/mixins/confirmation'
import CalculPrixEau from './CalculPrixEau'
import { mapActions } from 'vuex'

export default {
  name: 'Ilot',
  inject: ['$validator'],
  mixins: [mixinConfirmation],

  components: {
    SolIlotTable, CalculPrixEau
  },

  props: {
    idExploitation: {
      type: String,
      require: true
    },
    idIlot: {
      type: String,
      require: true
    }
  },

  data () {
    const ilotData = this.$store.getters.getIlotById(this.idIlot)
    return {
      ...ilotData,
      prix_irrigation: Number(ilotData.prix_irrigation),
      sols: this.buildLocalSols(),
      clearedIrrigationFlag: false
    }
  },

  created () {
    this.$store.commit('SET_CURRENT_ILOT', this.idIlot)
    this.$store.dispatch('referentiel/loadTypeMateriels')
    this.$store.dispatch('referentiel/loadTypeSols')
  },

  computed: {
    typeMateriel: function () {
      return this.$store.state.referentiel.typeMateriel.slice().sort((a, b) => a.nom.localeCompare(b.nom))
    },
    resultat_utilisation: function () {
      return this.debit_materiel * this.utilisation_materiel * (this.isEnrouleur ? (this.nombre_materiel || 1) : 1)
    },
    ilotTypeMateriel: function () {
      return this.typeMateriel.find(t => t.id_ref_type_materiel === this.id_ref_type_materiel)
    },
    isEnrouleur: function () {
      return this.ilotTypeMateriel && this.ilotTypeMateriel.code === 'enrouleur'
    },
    coutConso: function () {
      return this.ilotTypeMateriel ? this.ilotTypeMateriel.cout : null
    },
    somme_surface_sol () {
      this.$nextTick(() => {
        this.$validator.validate('somme_surface_ilot')
      })
      return this.sols.reduce((total, sol) => total + sol.surface, 0)
    },
    solsString () {
      return this.sols.map(s => s.id_ref_type_sol).join('-')
    },
    isFormModified () {
      return Object.keys(this.fields).some(key => this.fields[key].changed) || this.clearedIrrigationFlag
    },
    prixEau () {
      let objetRedevance = this.$store.getters['referentiel/getPrixById'](this.id_ref_prix_eau)
      return objetRedevance && this.coutConso ? parseFloat(objetRedevance.prix_eau) + parseFloat(this.coutConso) : null
    }
  },

  watch: {
    sau () {
      this.$nextTick(() => {
        this.$validator.validate('somme_surface_ilot')
      })
    },
    solsString: function () {
      this.fields['sols'].changed = true
    },
    prixEau (_newValue, oldValue) {
      if (oldValue == null) {
        return
      }
      this.$nextTick(() => {
        this.prix_irrigation = Number(this.prixEau)
      })
    }
  },

  beforeDestroy () {
    this.$store.commit('SET_CURRENT_ILOT', null)
  },

  methods: {
    ...mapActions('confirm', ['confirm']),
    ...mapActions('alerte', ['alerte']),
    save (clicked = false) {
      let p = new Promise((resolve, reject) => {
        this.$validator.validateAll().then((result) => {
          if (result) {
            this.$store.dispatch('saveIlot', {
              ...this.$data,
              deletedSol: this.calculDeletedSol()
            })
              .then(() => {
                this.sols = this.buildLocalSols()
                this.$store.dispatch('success', { message: 'Données enregistrées' })
                this.clearflags()
                this.clearedIrrigationFlag = false
                resolve()
              })
              .catch((error) => {
                reject(new Error("Erreur lors de l'enregistrement" + error))
              })
          } else {
            reject(new Error('Des erreurs sont présentes'))
          }
        })
      })

      if (clicked) {
        p.catch(err => {
          this.alerte({
            title: 'Erreur',
            message: err.message || err,
            okTest: 'Ok'
          })
        })
      }
      return p
    },
    addSol (typeSol) {
      this.sols.push({
        ...typeSol,
        surface: null
      })
      this.sols.sort((a, b) => a.nom.localeCompare(b.nom))
    },
    deleteSol (typeSol) {
      if (typeSol.id_sol) {
        this.deletedSol.push(typeSol.id_sol)
      }
      this.sols.splice(this.sols.findIndex(t => t.id_ref_type_sol === typeSol.id_ref_type_sol), 1)
    },
    buildLocalSols () {
      return this.$store.getters.getSolIlotById(this.idIlot)
        .map(value => {
          return { ...value }
        })
    },
    calculDeletedSol () {
      return this.$store.getters.getSolIlotById(this.idIlot)
        .map(value => value.id_ref_type_sol)
        .filter(id => this.sols.findIndex(s => s.id_ref_type_sol === id) === -1)
    },
    clearIrrigationData () {
      if (this.non_irrigue) {
        this.non_irrigue = false
      } else {
        this.confirm({
          title: 'Non irrigué',
          message: `En cochant cette case, les données de configuration du matériel et de paramétrage de l'irrigation qui ont pu être saisies ci-après seront perdues.`,
          okText: 'Changer'
        })
          .then(confirmation => {
            if (confirmation === true) {
              this.id_ref_type_materiel = null
              this.nombre_materiel = null
              this.debit_materiel = null
              this.utilisation_materiel = null
              this.prix_irrigation = null
              this.volume_irrigation = null
              this.date_debut_irrigation = null
              this.date_fin_irrigation = null
              this.clearedIrrigationFlag = true
              this.non_irrigue = true
            }
          })
      }
    }
  }
}

</script>
